import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useEffect, useId, useRef } from "react";

import { useT } from "../../context/locale";

import type {
  BetStatus,
  BetType,
  DrawStatus,
  TransactionStatus,
  UserStatus,
} from "../../types/api";

export { cx } from "./cx";
import { cx } from "./cx";

/* ── Button ───────────────────────────────────────────────────────────────── */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "gold";

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-full font-semibold uppercase " +
  "tracking-[0.07em] transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-45 " +
  "active:translate-y-px whitespace-nowrap";

const BUTTON_SIZES = {
  sm: "h-8 px-3.5 text-[11px]",
  md: "h-10 px-5 text-[12px]",
  lg: "h-12 px-7 text-[13px]",
};

export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  children,
  className,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: keyof typeof BUTTON_SIZES;
  loading?: boolean;
}) {
  const styles: Record<ButtonVariant, string> = {
    primary:
      "text-white shadow-[0_6px_18px_rgb(195_69_55/0.35)] hover:brightness-110",
    gold: "shadow-[0_6px_18px_rgb(231_167_64/0.3)] hover:brightness-110",
    secondary: "border hover:brightness-110",
    ghost: "hover:brightness-125",
    danger: "border hover:brightness-110",
  };

  const inline: Record<ButtonVariant, React.CSSProperties> = {
    primary: {
      background:
        "linear-gradient(180deg, var(--color-red-bright), var(--color-red))",
    },
    gold: {
      background:
        "linear-gradient(180deg, var(--color-gold-light), var(--color-gold-deep))",
      color: "#1b0a0d",
    },
    secondary: {
      background: "var(--surface-raised)",
      borderColor: "var(--border-strong)",
      color: "var(--text)",
    },
    ghost: { background: "transparent", color: "var(--text-muted)" },
    danger: {
      background: "var(--danger-bg)",
      borderColor: "var(--danger)",
      color: "var(--danger)",
    },
  };

  return (
    <button
      className={cx(
        BUTTON_BASE,
        BUTTON_SIZES[size],
        styles[variant],
        className,
      )}
      style={inline[variant]}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        "inline-block size-3.5 animate-spin rounded-full border-2 border-current",
        className,
      )}
      style={{ borderTopColor: "transparent" }}
      aria-hidden
    />
  );
}

/* ── Panel / Card ─────────────────────────────────────────────────────────── */

export function Panel({
  title,
  eyebrow,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  eyebrow?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cx("panel overflow-hidden", className)}>
      {(title || actions) && (
        <header
          className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3.5"
          style={{ borderColor: "var(--border)" }}
        >
          <div className="min-w-0">
            {eyebrow ? <p className="eyebrow mb-0.5">{eyebrow}</p> : null}
            {title ? (
              <h2
                className="display text-[17px]"
                style={{ color: "var(--accent)" }}
              >
                {title}
              </h2>
            ) : null}
          </div>
          {actions ? (
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </header>
      )}
      <div className={bodyClassName ?? "p-5"}>{children}</div>
    </section>
  );
}

/* ── Stat tile ────────────────────────────────────────────────────────────── */

export function StatTile({
  label,
  value,
  hint,
  tone = "gold",
  dot,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "gold" | "success" | "danger" | "info" | "plain";
  dot?: boolean;
}) {
  const colours: Record<string, string> = {
    gold: "var(--accent)",
    success: "var(--success)",
    danger: "var(--danger)",
    info: "var(--info)",
    plain: "var(--text)",
  };
  return (
    <div className="panel p-4">
      <div className="flex items-center gap-1.5">
        {dot ? (
          <span
            className="size-1.5 rounded-full"
            style={{ background: colours[tone] }}
            aria-hidden
          />
        ) : null}
        <p className="eyebrow">{label}</p>
      </div>
      <p
        className="money mt-2 text-[22px] leading-none"
        style={{ color: colours[tone] }}
      >
        {value}
      </p>
      {hint ? <p className="text-dim mt-1.5 text-[11px]">{hint}</p> : null}
    </div>
  );
}

/* ── Badges ───────────────────────────────────────────────────────────────── */

type Tone = "success" | "danger" | "warning" | "info" | "neutral" | "gold";

const TONE_STYLE: Record<Tone, React.CSSProperties> = {
  success: { background: "var(--success-bg)", color: "var(--success)" },
  danger: { background: "var(--danger-bg)", color: "var(--danger)" },
  warning: { background: "var(--warning-bg)", color: "var(--warning)" },
  info: { background: "var(--info-bg)", color: "var(--info)" },
  gold: { background: "rgb(231 167 64 / 0.14)", color: "var(--accent)" },
  neutral: { background: "var(--surface-inset)", color: "var(--text-dim)" },
};

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold " +
          "whitespace-nowrap uppercase tracking-[0.08em]",
        className,
      )}
      style={TONE_STYLE[tone]}
    >
      {children}
    </span>
  );
}

const USER_TONE: Record<UserStatus, Tone> = {
  active: "success",
  pending_review: "warning",
  banned: "danger",
  rejected: "danger",
};
export const UserStatusBadge = ({ status }: { status: UserStatus }) => {
  const t = useT();
  return <Badge tone={USER_TONE[status]}>{t(`status.${status}`)}</Badge>;
};

const DRAW_TONE: Record<DrawStatus, Tone> = {
  open: "success",
  locked: "warning",
  drawn: "info",
  settled: "neutral",
  void: "danger",
};
export const DrawStatusBadge = ({ status }: { status: DrawStatus }) => {
  const t = useT();
  return <Badge tone={DRAW_TONE[status]}>{t(`status.${status}`)}</Badge>;
};

const BET_TONE: Record<BetStatus, Tone> = {
  placed: "info",
  won: "success",
  lost: "neutral",
  refunded: "warning",
  voided: "danger",
};
export const BetStatusBadge = ({ status }: { status: BetStatus }) => {
  const t = useT();
  return <Badge tone={BET_TONE[status]}>{t(`status.${status}`)}</Badge>;
};

const TX_TONE: Record<TransactionStatus, Tone> = {
  success: "success",
  processing: "info",
  pending: "warning",
  failed: "danger",
  rejected: "danger",
};
export const TxStatusBadge = ({ status }: { status: TransactionStatus }) => {
  const t = useT();
  return <Badge tone={TX_TONE[status]}>{t(`status.${status}`)}</Badge>;
};

/** Coloured so a scan down the column groups them. */
const BET_TYPE_TONE: Record<BetType, Tone> = {
  number: "neutral",
  big: "danger",
  small: "info",
  odd: "gold",
  even: "success",
};

/**
 * A number pick shows the number itself — "7" tells the reader more than
 * "number" does, and the tone already says which family it belongs to.
 *
 * It reads as a distinct kind of bet, which it no longer quite is: a pick
 * stands for the half it falls in, so 7 is a way of backing Big. Showing the
 * number is still right — it is what the player chose — and the Draws screen is
 * where the rule is spelled out.
 */
export const BetTypeBadge = ({
  type,
  pickedNumber,
}: {
  type: BetType;
  pickedNumber?: number | null;
}) => {
  const t = useT();
  const label =
    type === "number" && pickedNumber !== null && pickedNumber !== undefined
      ? String(pickedNumber)
      : t(`bet.${type}`);
  return <Badge tone={BET_TYPE_TONE[type]}>{label}</Badge>;
};

export const TestBadge = () => {
  const t = useT();
  return <Badge tone="warning">{t("status.test")}</Badge>;
};

/**
 * Marks a round whose numbers an operator set, rather than the scheduler.
 *
 * Neutral, not a warning: setting a result by hand is a normal capability here.
 * The badge exists so the list still says where a number came from.
 */
export const ManualBadge = () => {
  const t = useT();
  return <Badge tone="neutral">{t("status.manual")}</Badge>;
};

/**
 * Marks a round written to carry generated history rather than one the scheduler
 * ran.
 *
 * Shown because the Draws table is the audit surface: these rounds are real
 * enough to bet against and to settle, but they sit in the past holding recent
 * numbers, and an operator reconciling the list needs to know which is which.
 * Nothing player-facing shows them at all.
 */
export const BackfilledBadge = () => {
  const t = useT();
  return <Badge tone="warning">{t("status.backfilled")}</Badge>;
};

/* ── Number chip ──────────────────────────────────────────────────────────── */

export function NumberChip({
  value,
  size = "md",
}: {
  value: number;
  size?: "sm" | "md";
}) {
  return (
    <span
      className="chip-number"
      style={
        size === "sm"
          ? { width: "1.4rem", height: "1.4rem", fontSize: 11 }
          : undefined
      }
    >
      {value}
    </span>
  );
}

/* ── Row actions ──────────────────────────────────────────────────────────── */

/**
 * The glyphs the row actions carry, by what the action *does*.
 *
 * Named by intent rather than by shape, so "the thing that removes a row" looks
 * the same on every screen without each page choosing a character. A label alone
 * is still the accessible name — the glyph is decorative and marked so.
 */
/** What a disabled chip shows instead of its own glyph — see `ActionButton`. */
const LOCKED_ICON = "⊘";

export const ActionIcon = {
  edit: "✎",
  delete: "✕",
  copy: "⧉",
  revoke: "⊗",
  reroll: "↻",
  view: "▤",
  approve: "✓",
  reject: "✕",
  credit: "⇄",
  history: "⚗",
  result: "⬡",
} as const;

type ActionTone = "neutral" | "primary" | "danger";

/**
 * The chip's classes, for a row action that has to be something other than a
 * `<button>` — in practice a router `<Link>`, since a `<button>` nested inside
 * an `<a>` is invalid markup and swallows one of the two click behaviours.
 *
 * Exported rather than solved with an `as` prop so the router stays out of the
 * UI library: the page already imports `Link` and only needs the styling.
 */
export function rowActionClass(tone: ActionTone = "neutral") {
  return cx(
    "row-action",
    tone === "danger" && "row-action-danger",
    tone === "primary" && "row-action-primary",
  );
}

/**
 * One control at the right-hand end of a table row.
 *
 * Distinct from `Button`, which is sized and weighted for a dialog footer or a
 * panel header. This is the table-density version: shorter, chip-shaped, and —
 * the reason it exists — with states that survive a dense row on both grounds.
 * A `variant="ghost"` Button in this position was transparent, muted and
 * borderless, which is to say it looked exactly like the data next to it.
 *
 * **Disabled is a first-class state here, not an afterthought.** `disabledWhy`
 * is required to disable one: an operator who cannot press something is owed
 * the reason, and the tooltip is where it goes. The chip keeps its label and
 * takes a dashed border and a padlock, so *which* action is unavailable stays
 * readable — dimming it into the background answers neither question.
 */
export function ActionButton({
  children,
  icon,
  tone = "neutral",
  disabledWhy,
  loading = false,
  className,
  title,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> & {
  icon?: string;
  tone?: ActionTone;
  /** Present ⇒ disabled. The text is the tooltip: say why, not just that. */
  disabledWhy?: string | null;
  loading?: boolean;
}) {
  const disabled = Boolean(disabledWhy) || loading;

  return (
    <button
      type="button"
      className={cx(
        "row-action",
        tone === "danger" && "row-action-danger",
        tone === "primary" && "row-action-primary",
        className,
      )}
      disabled={disabled}
      title={disabledWhy ?? title}
      {...rest}
    >
      {loading ? (
        <Spinner className="size-3" />
      ) : (
        <span className="row-action-icon" aria-hidden>
          {disabledWhy ? LOCKED_ICON : icon}
        </span>
      )}
      {children}
    </button>
  );
}

/**
 * The action cell's contents, pinned to the right-hand edge of the row.
 *
 * Right rather than left because it is where the eye ends up: the row is read
 * left to right and the decision comes last. Wrapping is allowed — a row with
 * four actions on a narrow window should stack them rather than force the table
 * wider and push the data out of view.
 */
export function RowActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {children}
    </div>
  );
}

/**
 * The placeholder for a row this operator may only look at.
 *
 * An empty cell reads as a table that failed to render, and leaves somebody
 * wondering whether to reload. A dash with a reason on it says the row is fine
 * and the permission is not.
 */
export function NoRowActions({ reason }: { reason?: string }) {
  const t = useT();
  return (
    <div className="flex justify-end">
      <span className="row-action-none" title={reason ?? t("action.noneAvailable")}>
        —
      </span>
    </div>
  );
}

/* ── Form controls ────────────────────────────────────────────────────────── */

/*
 * Everything form-related lives in `./form`. It is re-exported here so that
 * `from '../components/ui'` stays the single import for the console's UI, while
 * the controls themselves sit in one file that can be read end to end.
 */
export {
  Checkbox,
  DateField,
  Field,
  FieldGrid,
  Input,
  MoneyField,
  NumberField,
  NumberSlots,
  SegmentedControl,
  Select,
  SelectField,
  TextAreaField,
  TextField,
  Textarea,
  Toggle,
} from "./form";
export type { FieldProps, Option } from "./form";

/* ── Table ────────────────────────────────────────────────────────────────── */

export function Table({
  head,
  children,
  minWidth = "42rem",
}: {
  head: ReactNode[];
  children: ReactNode;
  /** Narrow tables in a side column can opt out of the default scroll width. */
  minWidth?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table
        className="w-full border-collapse text-[13px]"
        style={{ minWidth }}
      >
        <thead>
          <tr style={{ background: "var(--surface-inset)" }}>
            {head.map((cell, index) => (
              <th
                key={index}
                className="eyebrow border-b px-4 py-2.5 text-left whitespace-nowrap"
                style={{ borderColor: "var(--border)" }}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Tr({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <tr
      className={cx(
        "row-hover border-b transition-colors",
        onClick && "cursor-pointer",
      )}
      style={{ borderColor: "var(--border-soft)" }}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

export function Td({
  children,
  className,
  colSpan,
  style,
  title,
}: {
  children: ReactNode;
  className?: string;
  colSpan?: number;
  style?: React.CSSProperties;
  title?: string;
}) {
  return (
    <td
      className={cx("px-4 py-2.5 align-middle", className)}
      colSpan={colSpan}
      style={style}
      title={title}
    >
      {children}
    </td>
  );
}

/* ── Empty / loading / error states ───────────────────────────────────────── */

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <p className="display text-[15px]" style={{ color: "var(--text-muted)" }}>
        {title}
      </p>
      {detail ? (
        <p className="text-dim max-w-sm text-[12px]">{detail}</p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function LoadingRows({
  columns,
  rows = 5,
}: {
  columns: number;
  rows?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr
          key={rowIndex}
          className="border-b"
          style={{ borderColor: "var(--border-soft)" }}
        >
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <td key={columnIndex} className="px-4 py-3">
              <span
                className="block h-3 animate-pulse rounded"
                style={{
                  background: "var(--surface-inset)",
                  width: `${40 + ((rowIndex + columnIndex) % 4) * 15}%`,
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function ErrorNote({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  const t = useT();
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
      style={{ background: "var(--danger-bg)", borderColor: "var(--danger)" }}
    >
      <p className="text-[12px]" style={{ color: "var(--danger)" }}>
        {message}
      </p>
      {onRetry ? (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      ) : null}
    </div>
  );
}

/* ── Pagination ───────────────────────────────────────────────────────────── */

export function Pagination({
  page,
  pages,
  total,
  onChange,
}: {
  page: number;
  pages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const t = useT();
  if (total === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3"
      style={{ borderColor: "var(--border)" }}
    >
      <p className="text-dim text-[11px]">
        {t("common.page")} <span className="tnum">{page}</span> {t("common.of")}{" "}
        <span className="tnum">{pages}</span> ·{" "}
        {t("common.entries", { count: total.toLocaleString() })}
      </p>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          {t("common.previous")}
        </Button>
        <Button
          size="sm"
          disabled={page >= pages}
          onClick={() => onChange(page + 1)}
        >
          {t("common.next")}
        </Button>
      </div>
    </div>
  );
}

/* ── Modal ────────────────────────────────────────────────────────────────── */

export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  width = "32rem",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  eyebrow?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  /*
   * Every call site passes `onClose` as an inline arrow, so its identity changes
   * on each render. Listing it in the dependency array re-ran this effect on
   * every keystroke and the `focus()` below pulled focus out of whatever field
   * was being typed into — only the first character ever landed. The handler
   * lives in a ref so the effect can depend on `open` alone and run once per
   * opening, which is what "focus the dialog when it opens" actually means.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6"
      style={{ background: "rgb(0 0 0 / 0.66)", backdropFilter: "blur(3px)" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="panel max-h-[92vh] w-full overflow-y-auto rounded-b-none sm:rounded-b-[var(--radius-card)]"
        style={{ maxWidth: width }}
      >
        {/*
          Solid crimson with light text in both themes. The earlier gradient into
          `--surface` inverted in light mode and left the eyebrow unreadable.
        */}
        <header
          className="sticky top-0 z-10 border-b px-5 py-4"
          style={{
            borderColor: "var(--border-strong)",
            background: "var(--color-crimson)",
          }}
        >
          {eyebrow ? (
            <p
              className="mb-0.5 text-[10px] font-semibold tracking-[0.14em] uppercase"
              style={{ color: "var(--color-gold-light)" }}
            >
              {eyebrow}
            </p>
          ) : null}
          <h2
            id={titleId}
            className="display text-[18px]"
            style={{ color: "#fff" }}
          >
            {title}
          </h2>
        </header>
        <div className="p-5">{children}</div>
        {footer ? (
          <footer
            className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t px-5 py-4"
            style={{
              borderColor: "var(--border)",
              background: "var(--surface)",
            }}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
